import * as React from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import type { AskBlock, PromotionTarget, RunSummary } from '@/types/api';
import { CHAT_STATUS, CLIENT_CHAT_STATUS, PROMOTION_FAILED_MEANING, clientStatusNote, errorMessage } from '@/lib/copy';
import { useDocumentTitle } from '@/lib/hooks';
import { cn } from '@/lib/utils';
import { DetailLayout, PageBody, PageHeader } from '@/components/layout/page';
import { Flag, StateChip } from '@/components/state-chip';
import { VisibilitySplit } from '@/components/visibility-band';
import { Button } from '@/components/ui/button';
import { CopyValue } from '@/components/ui/copy-value';
import { Empty } from '@/components/ui/empty';
import { Panel, PanelBody, PanelHeader } from '@/components/ui/panel';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PromotionFailedNotice } from '@/components/ledger/promotion-ledger';
import { ClaimControl } from '@/features/queue/claim-control';
import { DiffViewer } from '@/features/diff/diff-viewer';
import { PromotionPanel } from '@/features/promotion/promotion-panel';
import { mergeMessages, useStreamStore } from '@/stores/stream-store';
import { AnswerAskDialog } from './answer-ask-dialog';
import { Composer } from './composer';
import { useExpertChat } from './queries';
import { RequestChangesDialog } from './request-changes-dialog';
import { DeployHistory, ReviewFacts } from './review-facts';
import { RunLedger } from './stream/run-ledger';
import { useExpertStream } from './stream/use-expert-stream';
import { Thread } from './thread';

/* ---------------------------------------------------------------------------
   THE REVIEW PAGE.

   Three surfaces on one chat, and they are tabs rather than columns for a
   reason: the aggregated diff needs the full width of the window, and a diff
   squeezed into a column is a diff nobody reads properly.

   THE HEADER IS THE PART THAT MUST NEVER BE ABSENT. The true status sits beside
   what the CLIENT is currently being shown, at all times. That pair is the
   second half of invariant I7 — the client's status is pinned across the whole
   review pipeline so they cannot infer the existence or timing of the hidden
   turns from a flickering status — and an expert writing a request-changes note
   needs to know what the client currently believes is happening. No other
   control on the page carries that.

   THE STREAM AND THE DOCUMENT. The stream owns the firehose; the query owns the
   document. They meet at `refetchToken`: a frame that moves something the stream
   does not carry — the composer predicate, the promotion gate, the diff — bumps
   it, and the effect below re-fetches rather than guessing at the new value.
--------------------------------------------------------------------------- */

type ReviewTab = 'thread' | 'diff' | 'promotion';

function isTab(value: string | null): value is ReviewTab {
  return value === 'thread' || value === 'diff' || value === 'promotion';
}

export function ReviewPage() {
  const params = useParams();
  const chatId = Number.parseInt(params.chatId ?? '', 10);
  const valid = Number.isFinite(chatId) && chatId > 0;

  const [search, setSearch] = useSearchParams();
  const tabParam = search.get('tab');
  const tab: ReviewTab = isTab(tabParam) ? tabParam : 'thread';
  const targetParam = search.get('target');
  const target: PromotionTarget = targetParam === 'production' ? 'production' : 'staging';

  const chat = useExpertChat(valid ? chatId : 0, valid);

  const attach = useStreamStore((s) => s.attach);
  const seed = useStreamStore((s) => s.seed);
  const refetchToken = useStreamStore((s) => s.refetchToken);
  const liveMessages = useStreamStore((s) => s.liveMessages);
  const deltas = useStreamStore((s) => s.deltas);
  const streamComposer = useStreamStore((s) => s.composer);
  const streamStatus = useStreamStore((s) => s.status);
  const streamPendingAsk = useStreamStore((s) => s.pendingAsk);
  const notices = useStreamStore((s) => s.notices);
  const unknown = useStreamStore((s) => s.unknown);

  React.useEffect(() => {
    if (valid) attach(chatId);
  }, [valid, chatId, attach]);

  // Seed the ledger from the document so a page load mid-run draws the run
  // before a single frame arrives. Every transition is monotonic, so the seed can
  // only ever be corrected forward.
  const data = chat.data;
  React.useEffect(() => {
    if (!data) return;
    seed({
      runs: (data.runs ?? []).filter((run): run is RunSummary => Boolean(run)),
      status: data.status,
      clientVisibleStatus: data.client_visible_status,
      composer: data.composer,
      pendingAsk: data.pending_ask ?? null,
      promotion: data.promotion ?? null,
    });
  }, [data, seed]);

  useExpertStream(valid ? chatId : null, data?.cursor?.stream_cursor);

  // The stream's instruction to re-read the document.
  const lastToken = React.useRef(refetchToken);
  React.useEffect(() => {
    if (refetchToken === lastToken.current) return;
    lastToken.current = refetchToken;
    void chat.refetch();
  }, [refetchToken, chat]);

  useDocumentTitle(data?.project_name ? `${data.project_name} · review` : 'Review');

  const setQuery = (next: { tab?: ReviewTab; target?: PromotionTarget }) => {
    const merged = new URLSearchParams(search);
    if (next.tab) merged.set('tab', next.tab);
    if (next.target) merged.set('target', next.target);
    setSearch(merged, { replace: true });
  };

  const [askOpen, setAskOpen] = React.useState<AskBlock | null>(null);
  const [changesOpen, setChangesOpen] = React.useState(false);

  if (!valid) {
    return (
      <PageBody>
        <Empty
          title="That is not a chat id"
          body="Open a review from the queue so the id comes from the row rather than from the address bar."
          action={
            <Button size="sm" variant="secondary" asChild>
              <Link to="/queue">Back to the queue</Link>
            </Button>
          }
        />
      </PageBody>
    );
  }

  if (chat.isLoading) return <ReviewSkeleton />;

  if (chat.isError || !data) {
    return (
      <PageBody>
        <Empty
          title="This review did not load"
          body={
            chat.error
              ? errorMessage(chat.error.code, chat.error.message)
              : 'The chat could not be read. It may have left review, or it may never have been assigned to you.'
          }
          action={
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={() => void chat.refetch()}>
                Retry
              </Button>
              <Button size="sm" variant="ghost" asChild>
                <Link to="/queue">Back to the queue</Link>
              </Button>
            </div>
          }
        />
      </PageBody>
    );
  }

  const status = streamStatus ?? data.status;
  const composer = streamComposer ?? data.composer;
  const claim = data.claim ?? null;
  const mine = claim?.is_mine === true;
  const messages = mergeMessages(data.messages, liveMessages);
  const pendingAsk = streamPendingAsk ?? (data.pending_ask ? { ask: data.pending_ask, answerableByExpert: true } : null);
  const answerableAsk = pendingAsk?.answerableByExpert ? pendingAsk.ask : null;
  const promotionFailed = status === 'promotion failed';

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'Review queue', to: '/queue' }, { label: `chat ${data.id}` }]}
        title={data.project_name ?? `Project ${data.project_id}`}
        identity={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <CopyValue value={String(data.id)} display={`chat ${data.id}`} />
            <span aria-hidden className="text-ink-3">
              ·
            </span>
            <Link to="/projects" className="text-ink-3 underline underline-offset-2 hover:text-ink-2">
              project {data.project_id}
            </Link>
          </span>
        }
        state={
          <span className="flex flex-wrap items-center gap-2">
            <StateChip state={CHAT_STATUS[status]} />
            {data.context_exhausted ? (
              <Flag
                label="1M"
                tone="warn"
                title="Context ceiling reached. The composer is locked for both roles; Done and every promotion action stay available."
              />
            ) : null}
            {promotionFailed ? <Flag label="operator" tone="danger" title={PROMOTION_FAILED_MEANING} /> : null}
          </span>
        }
        actions={
          <>
            <ClaimControl chatId={data.id} claim={claim} claimable={status === 'pending review'} size="md" />
            <Button
              variant="secondary"
              disabled={!mine}
              title={
                mine
                  ? 'Hand the thread back to the client. This unfreezes the project and releases your claim.'
                  : 'Claim this chat before handing it back.'
              }
              onClick={() => setChangesOpen(true)}
            >
              Request changes
            </Button>
          </>
        }
      >
        <VisibilitySplit
          yours={<StateChip size="sm" state={CHAT_STATUS[status]} />}
          theirs={
            <span className="font-mono text-xs text-ink-2">{CLIENT_CHAT_STATUS[data.client_visible_status]}</span>
          }
          note={data.client_visible_status_note ?? clientStatusNote(status, data.client_visible_status)}
        />
      </PageHeader>

      <PageBody className="space-y-4">
        {promotionFailed ? (
          <PromotionFailedNotice failure={data.promotion?.failure ?? null} />
        ) : null}

        <Tabs
          value={tab}
          onValueChange={(value) => {
            if (isTab(value)) setQuery({ tab: value });
          }}
        >
          {/* The parked-ask affordance sits beside the tab list rather than
              inside it: a Radix tab list owns roving focus, and a button in it
              would steal an arrow key from the tabs. */}
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b border-rule">
            <TabsList className="border-b-0">
              <TabsTrigger value="thread">Thread</TabsTrigger>
              <TabsTrigger value="diff">Diff</TabsTrigger>
              <TabsTrigger value="promotion">Promotion</TabsTrigger>
            </TabsList>
            {answerableAsk ? (
              <span className="flex items-center gap-2 pb-1 pr-1">
                <span className="font-mono text-tiny uppercase tracking-[0.08em] text-warn">question parked</span>
                <Button size="sm" variant="secondary" onClick={() => setAskOpen(answerableAsk)}>
                  Answer
                </Button>
              </span>
            ) : null}
          </div>

          <TabsContent value="thread" className="pt-4">
            <DetailLayout
              main={
                <>
                  <Panel>
                    <PanelHeader title="Thread" meta={`${messages.length} turns`} />
                    <PanelBody>
                      <Thread
                        messages={messages}
                        deltas={deltas}
                        pendingAskId={answerableAsk?.ask_id ?? null}
                        onAnswerAsk={(ask) => setAskOpen(ask)}
                      />
                    </PanelBody>
                    <div className="border-t border-rule p-3">
                      <Composer chatId={data.id} composer={composer} />
                    </div>
                  </Panel>

                  {notices.length > 0 ? <NoticeLog /> : null}
                  {unknown.length > 0 ? <DriftLog /> : null}
                </>
              }
              aside={
                <>
                  <ReviewFacts chat={data} />
                  <Panel>
                    <PanelHeader title="Run ledger" />
                    <PanelBody>
                      <RunLedger />
                    </PanelBody>
                  </Panel>
                  <DeployHistory chat={data} />
                </>
              }
            />
          </TabsContent>

          <TabsContent value="diff" className="pt-4">
            <DiffViewer
              chatId={data.id}
              target={target}
              onTargetChange={(next) => setQuery({ target: next })}
            />
          </TabsContent>

          <TabsContent value="promotion" className="pt-4">
            <DetailLayout
              main={<PromotionPanel chat={data} />}
              aside={
                <>
                  <ReviewFacts chat={data} />
                  <DeployHistory chat={data} />
                </>
              }
            />
          </TabsContent>
        </Tabs>
      </PageBody>

      <AnswerAskDialog chatId={data.id} ask={askOpen} onClose={() => setAskOpen(null)} />
      <RequestChangesDialog chatId={data.id} open={changesOpen} onOpenChange={setChangesOpen} />
    </>
  );
}

/**
 * Operational notices from the stream — a retry, a mirror re-fetch, a lease
 * renewal. Expert-only by contract, and worth a log rather than a toast: they
 * are the trail an expert reads when something took longer than it should have.
 */
function NoticeLog() {
  const notices = useStreamStore((s) => s.notices);
  const dismiss = useStreamStore((s) => s.dismissNotice);

  return (
    <Panel>
      <PanelHeader title="Platform notices" meta={`${notices.length}`} />
      <ul className="m-0 list-none divide-y divide-rule p-0">
        {[...notices].reverse().map((notice) => (
          <li key={notice.id} className="flex items-baseline gap-2 px-3 py-1.5">
            <span
              aria-hidden
              className={cn(
                'mt-[6px] h-[5px] w-[5px] shrink-0 rounded-[1px]',
                notice.level === 'error' && 'bg-danger',
                notice.level === 'warning' && 'bg-warn',
                notice.level === 'info' && 'bg-ink-3',
              )}
            />
            <span className="min-w-0 flex-1 text-xs leading-[17px] text-ink-2">{notice.message}</span>
            {notice.code ? <span className="shrink-0 font-mono text-tiny text-ink-3">{notice.code}</span> : null}
            <button
              type="button"
              onClick={() => dismiss(notice.id)}
              className="shrink-0 font-mono text-tiny text-ink-3 hover:text-ink"
              aria-label="Dismiss notice"
            >
              ×
            </button>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

/**
 * Contract drift, collected.
 *
 * A stream kind this build has no mapping for is not rendered as a turn, because
 * drawing it would assert whether the client can see it. It is collected here
 * instead, with its kind and nothing else — never the payload, because the whole
 * point is that we do not know what it is.
 */
function DriftLog() {
  const unknown = useStreamStore((s) => s.unknown);

  return (
    <Panel className="border-danger">
      <PanelHeader title="Unrecognised stream frames" meta={`${unknown.length}`} />
      <PanelBody className="space-y-1.5">
        <p className="text-xs leading-[17px] text-ink-2">
          This build has no mapping for the frames below, so they were not drawn. The expert stream is not filtered, so
          this is a build that is behind the contract rather than a scrub bug — regenerate the types and add the case.
        </p>
        <ul className="m-0 list-none p-0">
          {unknown.map((frame) => (
            <li key={frame.id} className="font-mono text-tiny text-danger">
              {frame.kind}
            </li>
          ))}
        </ul>
      </PanelBody>
    </Panel>
  );
}

function ReviewSkeleton() {
  return (
    <>
      <div className="border-b border-rule bg-surface px-4 pb-3 pt-3.5 sm:px-6">
        <Skeleton className="h-2.5 w-40" />
        <Skeleton className="mt-3 h-5 w-64" />
        <Skeleton className="mt-2 h-3 w-48" />
      </div>
      <PageBody>
        <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,1fr)_310px]">
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className={cn('h-16', i % 2 ? 'ml-3' : '')} />
            ))}
          </div>
          <div className="space-y-3">
            <Skeleton className="h-48" />
            <Skeleton className="h-32" />
          </div>
        </div>
      </PageBody>
    </>
  );
}
